import { useEffect, useRef, useState } from 'react';
import { useActiveConversation } from '../components/chat/ActiveConversationContext';
import { bindSiteChat, openSitePane } from './site-edit-store';
import { SITE_EDIT_PARAM, siteEditArrival, siteEditTarget, type SiteEditArrival } from './site-edit-arrival';
import { stripParams } from './strip-url-params';
import { NARROW_BREAKPOINT_PX } from './use-narrow';
import { useSitePaneFits } from './use-site-pane';
import { useWorkspaces } from './workspaces';

/** How long an arrival waits for the Spaces to name its site before it settles for the chat alone. */
export const SITE_EDIT_ARRIVAL_WAIT_MS = 20_000;

/**
 * Acts on a site-edit arrival (`lib/site-edit-arrival.ts`) once: when the chat shows the arrival's conversation and the
 * Spaces have loaded, the chat is bound to its site and the site's preview pane opens beside it. The param is stripped
 * at once, so a reload or a copied link does not open the pane again over a closed one.
 *
 * Computer only, by construction: where the chat cannot host the pane (a phone, a narrow or stacked window) the arrival
 * opens the chat alone, exactly as the link does in the iOS and Android apps, which take it as a plain chat app link.
 */
export function useSiteEditArrival(): void {
  const [arrival] = useState<SiteEditArrival | null>(() => (typeof window === 'undefined' ? null : siteEditArrival(window.location.search)));
  const done = useRef(false);
  const startedAt = useRef(Date.now());
  const spaces = useWorkspaces();
  const { activeConversationId } = useActiveConversation();
  const fits = useSitePaneFits();

  useEffect(() => {
    if (arrival) stripParams([SITE_EDIT_PARAM]);
  }, [arrival]);

  useEffect(() => {
    if (!arrival || done.current || spaces.status === 'loading') return;
    // `useNarrow` reads wide on the first frame and corrects after paint: a phone is asked directly too.
    if (spaces.status === 'error' || !fits || window.matchMedia(`(max-width: ${NARROW_BREAKPOINT_PX - 1}px)`).matches) {
      done.current = true;
      return;
    }
    if (activeConversationId !== arrival.conversationId) return;
    const target = siteEditTarget(spaces.workspaces, arrival.metaSiteId);
    // The Spaces may be last visit's list while the server's is on its way, and a site just built is not in it yet:
    // stay pending and look again as the list updates, until it has had time to arrive.
    if (!target) {
      if (Date.now() - startedAt.current > SITE_EDIT_ARRIVAL_WAIT_MS) done.current = true;
      return;
    }
    done.current = true;
    bindSiteChat(arrival.conversationId, target);
    openSitePane({ ...target, conversationId: arrival.conversationId });
  }, [arrival, spaces, activeConversationId, fits]);
}
